import { ModelInput, ModelOutput } from '@/tools/models/model.store';
import {
  ColumnDefinition,
  EditorType,
  ModelToSelect,
  TABLE_DEFINITION_COLUMN_OPTIONS,
} from '@/tableDefinitionEditor/tableDefinition.types';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import { resetModel, setModel } from '@/tools/models/model.actions';
import {
  ColumnDefinitionBulkEditInputV1,
  ColumnDefinitionInputV1,
  DerivedFeatureV1,
  InstanceOutputMappingV1,
  ModelFromIdsInputV1,
  OneOffModelApplicationInputV1,
  ReferencedOutputItemV1,
  sqItemsApi,
  sqModelsApi,
  TableDefinitionOutputV1,
  TableModelApplicationInputV1,
} from '@/sdk';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { MODEL_TYPES, ModelType } from '@/tools/models/model.types';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { DEFAULT_ITEM_ID_COLUMN_NAME } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.atom';
import {
  columnDefinitionOutputToColumnDefinitionInput,
  getExistingTableDefinition,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { addOrUpdateColumn, addOrUpdateColumns } from '@/tableDefinitionEditor/tableGridColumns.utilities';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { getDependencies } from '@/utilities/formula.utilities';
import { fetchModels } from '@/workbook/workbook.actions';
import { closeInvestigationTool, setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { OutputTypeEnum } from '@/sdk/model/InputFeatureV1';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import {
  setColumnIdToBeUsedAsModelInput,
  setIsApplyingModel,
  setModelInputToSelect,
  setTableDefinition,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import { setSelectedColumnOption } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { ModelApplicationMappingV1 } from 'sdk/model/ModelApplicationMappingV1';
import { SelectOptionIF } from '@/usage/Usage.page';
import { Datasource } from '@/search/search.types';
import { addTrendItem } from '@/trendData/trend.actions';

/**
 * Creates a new model in the backend from a model definition derived from column definitions.
 */
export const createModelFromColumnDefinitions = async (model: ModelFromIdsInputV1) => {
  const { data: modelOutput } = await sqModelsApi.createModelFromTableColumnDefinitions(model);
  return modelOutput;
};

/**
 * Applies a selected model to the current scaling table by creating new columns for each derived feature in the model.
 */
export const applyModelToScalingTable = async (
  modelId: string,
  tableDefinitionId: string,
  input: TableModelApplicationInputV1,
) => {
  const { data: modelOutput } = await sqModelsApi.applyModelToTable(input, { modelId, tableDefinitionId });
  return modelOutput;
};

/** Fetches a model by ID and sets it in the appropriate model store (Scaling or Tool) */
export const fetchModel = async (id: string, modelType: ModelType) => {
  try {
    const { data: model } = await sqModelsApi.getModel({ modelId: id });
    setModel(modelType, model);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

const formatDerivedColumns = async (
  existingTable: TableDefinitionOutputV1 | undefined,
  derivedFeatureNames: string[],
  columns: ColumnDefinition[],
  isOutput: boolean,
) => {
  const outputColumns = existingTable ? existingTable.columnDefinitions : columns;
  const formattedColumns: ColumnDefinitionBulkEditInputV1[] = (existingTable?.columnDefinitions ?? [])
    .filter((col) => (derivedFeatureNames.includes(col.columnName) && isOutput ? !col.isHidden : col.isHidden))
    .map((col) =>
      columnDefinitionOutputToColumnDefinitionInput(
        { ...col, displayProperty: SeeqNames.Properties.Name },
        outputColumns,
      ),
    );
  return formattedColumns;
};

/** Adds columns for all derived features in the model to the scaling table. */
export const addModelColumnsToTable = async (
  tableDefinitionId: string,
  derivedFeatureNames: string[],
  columns: ColumnDefinition[],
  editorType: EditorType,
) => {
  let existingTable = await getExistingTableDefinition(tableDefinitionId, editorType, setTableDefinition);

  const intermediateColumns: ColumnDefinitionBulkEditInputV1[] = await formatDerivedColumns(
    existingTable,
    derivedFeatureNames,
    columns,
    false,
  );
  if (intermediateColumns.length) {
    await addOrUpdateColumns(intermediateColumns, tableDefinitionId, editorType);
    // Get the most up-to-date columns to prevent errors with rule indexes when adding the outputs
    existingTable = await getExistingTableDefinition(tableDefinitionId, editorType, setTableDefinition);
  }

  const outputColumns: ColumnDefinitionBulkEditInputV1[] = await formatDerivedColumns(
    existingTable,
    derivedFeatureNames,
    columns,
    true,
  );
  if (outputColumns.length) {
    await addOrUpdateColumns(outputColumns, tableDefinitionId, editorType);
  }
};

/** Recursively finds all input and intermediate column IDs for a given starting column ID */
const getInputAndIntermediateIds = (
  startId: string,
  visitedIds: string[],
  inputs: string[],
  intermediates: string[],
  outputIds: string[],
  columnIdMap: Map<string, ColumnDefinition>,
) => {
  if (visitedIds.includes(startId)) {
    return;
  }
  visitedIds.push(startId);

  const col = columnIdMap.get(startId);
  if (!col) {
    return;
  }

  const directInputs: string[] = [];
  const isSearchItemsDisplayName = (name: string) => name === DEFAULT_ITEM_ID_COLUMN_NAME;
  col.rules?.forEach((r) => r.inputs?.forEach((inp) => directInputs.push(inp)));

  if (directInputs.length === 0) {
    if (!isSearchItemsDisplayName(col.displayName) && !inputs.includes(startId)) {
      inputs.push(startId);
    }
    return;
  }

  if (!isValidOutputColumn(col) && !inputs.includes(startId) && !isSearchItemsDisplayName(col.displayName)) {
    inputs.push(startId);
  } else if (!outputIds.includes(startId) && !intermediates.includes(startId) && isValidOutputColumn(col)) {
    intermediates.push(startId);
  }

  directInputs.forEach((depId) =>
    getInputAndIntermediateIds(depId, visitedIds, inputs, intermediates, outputIds, columnIdMap),
  );
};

const toModelInputsFromTable = (
  ids: string[],
  inputs: ModelInput[],
  columnIdMap: Map<string, ColumnDefinition>,
): ModelInput[] => {
  const existingById = new Map((inputs ?? []).map((i) => [i.referenceId, i]));

  return ids
    .map((id) => {
      const existing = existingById.get(id);
      if (existing) {
        return existing;
      }

      const col = columnIdMap.get(id);
      if (!col) {
        return null;
      }

      return {
        referenceId: col.id,
        name: col.displayName,
        isCollapsed: false,
        description: col.description || '',
        originalName: col.displayName,
      } as ModelInput;
    })
    .filter((input): input is ModelInput => !!input);
};

const toModelIntermediatesFromTable = (
  ids: string[],
  columnIdMap: Map<string, ColumnDefinition>,
): ReferencedOutputItemV1[] =>
  ids
    .map((id) => columnIdMap.get(id))
    .filter((c): c is ColumnDefinition => !!c)
    .map((column) => ({
      referenceId: column.id,
      name: column.displayName,
      isOutput: false,
    }));

/** Given a set of output columns, recursively finds all input columns and intermediate columns (those that are neither
 *  inputs nor outputs) and sets the model state accordingly. */
export const getModelInputsAndIntermediatesFromTable = (
  outputColumns: ColumnDefinition[],
  inputs: ModelInput[],
  editorType: EditorType,
) => {
  const modelType = MODEL_TYPES.SCALING;
  const tableDefinitionStore = getTableDefinitionStore(editorType);
  if (!outputColumns?.length) {
    return;
  }

  const columnIdMap = new Map<string, ColumnDefinition>(tableDefinitionStore.columns.map((c) => [c.id, c]));
  const outputIds = outputColumns.map((c) => c.id);

  const inputIds: string[] = [];
  const intermediateIds: string[] = [];
  outputColumns.forEach((col) => {
    getInputAndIntermediateIds(col.id as string, [], inputIds, intermediateIds, outputIds, columnIdMap);
  });

  setModel(modelType, {
    inputFeatures: toModelInputsFromTable(inputIds, inputs, columnIdMap),
    derivedFeatures: [
      ...outputColumns,
      ...toModelIntermediatesFromTable(intermediateIds, columnIdMap),
    ] as ReferencedOutputItemV1[],
  });
};

export const hasCollapsedInputs = (inputs: ModelInput[]): boolean => inputs.some((i) => !i.isCollapsed);

export const hasExpandedInputs = (inputs: ModelInput[]): boolean => inputs.some((i) => i.isCollapsed);

const updateAllInputsCollapse = (inputs: ModelInput[], collapsed: boolean, modelType: ModelType) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setModel(modelType, { inputFeatures: updated });
};

export const expandAllInputs = (inputs: ModelInput[], modelType: ModelType) =>
  updateAllInputsCollapse(inputs, false, modelType);

export const collapseAllInputs = (inputs: ModelInput[], modelType: ModelType) =>
  updateAllInputsCollapse(inputs, true, modelType);

/** Validates that a column can be used as a model output. Only columns with rules of type 'formulaCreator' are
 *  supported in the backend, for now. */
export const isValidOutputColumn = (column: ColumnDefinition): boolean => {
  const validRules = [SeeqNames.MaterializedTables.Rules.FormulaCreator];

  return column.rules.every((rule) =>
    validRules.includes(rule.rule as typeof SeeqNames.MaterializedTables.Rules.FormulaCreator),
  );
};

// TODO CRAB-51125: Properly display fallback rules, not just the first rule
/**
 * Converts the features of a model into options to be display in the Formula Item Table
 *
 * @param {DerivedFeatureV1[]} features - The features to convert to options
 * @param {boolean} isOutput - True if the features are outputs, false if intermediates
 * @return The options for the Formula Item Table
 */
export const displayModelFeatures = (features: DerivedFeatureV1[], isOutput: boolean): FormulaItemTableOption[] =>
  features
    .filter((f) => f.isOutput === isOutput)
    .map((f) => ({
      key: f.name,
      display: f.name,
      formula: f.rules?.[0]?.formulaCreator?.formula?.argument ?? '',
    })) as unknown as FormulaItemTableOption[];

/** Updates a specific input in the model's input features list
 *
 * @param patch - the fields to update in the input
 * @param input - the input to update
 * @param inputFeatures - the current list of input features
 * @param modelType - the type of model being updated (Scaling or Tool)
 */
export const updateInput = (
  patch: Partial<ModelInput>,
  input: ModelInput,
  inputFeatures: ModelInput[],
  modelType: ModelType,
) => {
  const idToUse = input.referenceId ?? input.id;
  if (!idToUse) {
    return;
  }

  let updated = false;
  const updatedFeatures = inputFeatures.map((input) => {
    const inputId = input.referenceId ?? input.id;
    if (!updated && inputId === idToUse) {
      updated = true;
      return { ...input, ...patch };
    }
    return input;
  });

  setModel(modelType, { inputFeatures: updatedFeatures });
};

/** Validates that none of the derived feature names already exist in the scaling table column names.
 * If any do, throws an error listing the duplicates.
 *
 * @param scalingTableColumnNames - the existing column names in the scaling table
 * @param derivedFeatureNames - the names of the derived features in the model
 */
export const validateModelOutputNames = (scalingTableColumnNames: string[], derivedFeatureNames: string[]) => {
  const outputNamesInTable = Array.from(
    new Set(derivedFeatureNames.filter((name) => scalingTableColumnNames.includes(name))),
  );

  if (outputNamesInTable.length) {
    const listOfNames = outputNamesInTable.map((name) => `"${name}"`).join(', ');
    const namesPlural = outputNamesInTable.length > 1 ? 'names' : 'name';
    const colsPlural = outputNamesInTable.length > 1 ? 'columns' : 'column';

    throw new Error(
      `Applying this model would create duplicate column ${namesPlural}: ${listOfNames}. ` +
        `Please rename the conflicting table ${colsPlural} and try again.`,
    );
  }
};

/** Fetches all items of the specified types, decorates them, and adds any new ones
 *  to the model's derived features list.
 *
 * @param derivedFeatures - The current list of derived features in the model
 * @param setOutputs - Function to update the model's derived features in state
 * @param outputs - The current list of derived features in state
 * */
export function setAllDetailsPaneItemsInModel(
  derivedFeatures: ModelOutput[],
  setOutputs: (output: ModelOutput[]) => void,
  outputs: ModelOutput[],
) {
  const items = getAllItems({
    excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
    itemTypes: [ITEM_TYPES.CONDITION, ITEM_TYPES.SERIES],
  }).filter((item) => item.calculationType !== 'periodic-condition');

  const decorated = decorate({ items });
  const decoratedArray = Array.isArray(decorated) ? decorated : [decorated];
  const existingIds = derivedFeatures.map((df) => df.id);

  const visitedIds: string[] = [];
  const allOutputs = decoratedArray.filter((item) => {
    if (!item.id || visitedIds.includes(item.id)) {
      return false;
    }
    visitedIds.push(item.id);
    return true;
  });

  if (allOutputs.length === 0) {
    return;
  }

  const outputsToAdd = allOutputs.filter(
    (output) => !existingIds.includes(output.id) && output.calculationType !== undefined,
  );

  if (outputsToAdd.length) {
    setOutputs([...outputs, ...outputsToAdd]);
    setModel(MODEL_TYPES.TOOL, { derivedFeatures: [...derivedFeatures, ...outputsToAdd] });
  }
}

const toModelIntermediatesFromItems = (items: any[]): ReferencedOutputItemV1[] =>
  items.map((item) => ({
    referenceId: item.id,
    name: item.name,
    isOutput: false,
  }));

/** Given a set of model outputs, fetches their dependencies and finds all input and intermediate items.
 *  Sets the model state accordingly.
 *
 *  @param outputs - The current model outputs to derive inputs and intermediates from
 *  @param inputs - The current model inputs to avoid duplicates
 *  */
export const getModelToolInputsAndIntermediates = (outputs: ModelOutput[], inputs: ModelInput[]) => {
  const updatedInputs: ModelInput[] = [];
  const parentIds = new Set<string>();
  const outputIds = outputs.map((output) => output.id);
  const modelType = MODEL_TYPES.TOOL;

  const promises = outputIds.map((outputId) =>
    getDependencies({ id: outputId as string }).then((deps) => {
      deps?.dependencies.forEach((dep: any) => {
        const updatedInputIds = updatedInputs.map((input) => input.id);
        if (!updatedInputIds.includes(dep.id)) {
          const lastAncestor = dep.ancestors?.length ? dep.ancestors[dep.ancestors.length - 1].name : null;
          updatedInputs.push({
            name: dep.name,
            originalName: dep.name,
            id: dep.id,
            inputType: dep.type,
            description: '',
            parent: lastAncestor,
            isCollapsed: false,
            referenceId: dep.id,
          } as unknown as ModelInput);
        }

        // Make sure we are not adding items that are dependent on other inputs. We only want "root" level inputs.
        // This is done by tracking all items that are parameters of other items and then filtering them out later.
        if (dep.parameterOf?.length) {
          dep.parameterOf.forEach((parameter: { id: string }) => {
            if (parameter?.id) {
              parentIds.add(parameter.id);
            }
          });
        }
      });
    }),
  );

  Promise.all(promises).then(() => {
    const validInputs = updatedInputs
      .filter((input) => input?.id && !parentIds.has(input.id))
      .filter((input) => input.inputType !== SeeqNames.Types.CalculatedCondition);
    const newInputs: ModelInput[] = validInputs.filter((validInput) => {
      return !!validInput.id && !inputs.some((input) => input.id === validInput.id);
    });
    const existingInputs: ModelInput[] = inputs.filter((input) =>
      validInputs.some((validInput) => validInput.id === input.id),
    );

    const intermediates = updatedInputs.filter(
      (input) => input.inputType !== SeeqNames.Types.StoredSignal && !outputIds.includes(input.id),
    );

    setModel(modelType, {
      inputFeatures: [...existingInputs, ...newInputs],
      derivedFeatures: [...outputs, ...toModelIntermediatesFromItems(intermediates)] as ModelOutput[],
    });
  });
};

/**
 * Creates a new model in the backend from a model definition derived from items.
 */
export const createModelFromItems = async (model: ModelFromIdsInputV1) => {
  const { data: modelOutput } = await sqModelsApi.createModelFromItems(model);
  return modelOutput;
};

export const getInputIcon = (type: string | undefined) => {
  switch (type) {
    case SeeqNames.Types.StoredCondition:
      return "<i class='fc fc-capsule-set sq-icon-color ml3'></i>";
    case SeeqNames.Types.StoredSignal:
      return "<i class='fc fc-series sq-icon-color ml3'></i>";
    case SeeqNames.Types.CalculatedScalar:
      return "<i class='fc fc-scalar sq-icon-color ml3'></i>";
    default:
      return '';
  }
};

export const toReferencedOutputItems = (derivedFeatures: ModelOutput[]): ReferencedOutputItemV1[] => {
  const referencedOutputItems = derivedFeatures.map((feature) => {
    if ('id' in feature && 'name' in feature) {
      return {
        referenceId: feature.id,
        name: feature.name,
        isOutput: true, // Selected details pane items are always outputs
      } as ReferencedOutputItemV1;
    }
    // For intermediates, they have already been formatted correctly
    return feature;
  }) as ReferencedOutputItemV1[];
  return referencedOutputItems;
};

export const executeModelFromItemsCreation = async (
  description: string,
  inputFeatures: ModelInput[],
  derivedFeatures: ModelOutput[],
  workbookId: string,
  name: string,
) => {
  try {
    await createModelFromItems({
      name,
      description,
      referencedInputItems: inputFeatures.map((i) => ({
        name: i.name,
        description: i?.description,
        referenceId: i.referenceId,
      })),
      referencedOutputItems: toReferencedOutputItems(derivedFeatures as ModelOutput[]),
      scopedTo: workbookId,
    });

    await fetchModels([workbookId]);
    successToast({ messageKey: 'MODELS.CREATE_MESSAGE' });
    resetModel(MODEL_TYPES.TOOL);
    setActiveTool(TREND_TOOLS.MODELS);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const getValidColumnType = (outputType: OutputTypeEnum | undefined) => {
  switch (outputType) {
    case OutputTypeEnum.ITEMID:
      return ColumnTypeEnum.UUID;
    case OutputTypeEnum.NUMERIC:
      return ColumnTypeEnum.NUMERIC;
    case OutputTypeEnum.TEXT:
      return ColumnTypeEnum.TEXT;
    default:
      return null;
  }
};

export const resetModelApplication = (editorType: EditorType) => {
  setIsApplyingModel(editorType, false);
  setColumnIdToBeUsedAsModelInput(editorType, undefined);
  setModelInputToSelect(editorType, undefined);
};

/** Adds a newly created input column to the scaling table and updates the corresponding input feature in the model's
 *  input features list.
 */
export const updateNewlyCreatedModelInput = async (
  editorType: EditorType,
  tableDefinitionId: string,
  columnDefinition: ColumnDefinitionInputV1,
  modelInputToSelect: ModelToSelect | undefined,
  inputFeatures: ModelInput[],
) => {
  try {
    const output = await addOrUpdateColumn(editorType, tableDefinitionId, columnDefinition, undefined);
    if (output) {
      const colDefs = output.tableDefinition.columnDefinitions;
      const addedColumn = colDefs[colDefs?.length - 1];
      if (!addedColumn) {
        return;
      }

      setColumnIdToBeUsedAsModelInput(editorType, addedColumn.id);

      if (modelInputToSelect) {
        const foundColumn = colDefs.find((column) => column.id === addedColumn.id);
        const inputFeatureToUpdate = inputFeatures.find((input) => input.id === modelInputToSelect.id);

        if (foundColumn && inputFeatureToUpdate) {
          updateInput(
            {
              columnDefinition: {
                ...foundColumn,
                displayProperty: SeeqNames.Properties.Name,
              } as ColumnDefinition,
            },
            inputFeatureToUpdate,
            inputFeatures,
            MODEL_TYPES.SCALING,
          );
        }
      }

      setSelectedColumnOption(editorType, TABLE_DEFINITION_COLUMN_OPTIONS.APPLY_MODEL);
    }
    return;
  } catch (err) {
    errorToast({ httpResponseOrError: err });
  }
};

/**
 * Applies a selected model once and creates the derived features as calculated items.
 */
export const applyModelOnce = async (modelId: string, input: OneOffModelApplicationInputV1) => {
  const { data: modelOutput } = await sqModelsApi.applyModel(input, { modelId });
  return modelOutput;
};

/** Adds items created from applying a model to the trend. */
export const addModelItemsToTrend = async (outputs: InstanceOutputMappingV1[]) => {
  const outputIds = outputs.map((output) => output.outputId);

  const items = await Promise.all(
    outputIds.map(async (id) => {
      if (!id) {
        return;
      }
      const { data: itemOutput } = await sqItemsApi.getItemAndAllProperties({ id });
      return itemOutput;
    }),
  );

  items.forEach((item) => {
    if (item) {
      addTrendItem(item);
    }
  });
};

/** Applies a model once using the specified input features and adds the resulting items to the details pane. */
export const executeSingleModelApplication = async (
  modelId: string,
  inputFeatures: ModelInput[],
  workbookId: string,
  selectedDatasource: SelectOptionIF<Datasource>,
) => {
  try {
    const inputMappings: ModelApplicationMappingV1[] = inputFeatures.map((input) => ({
      featureId: input.id,
      itemIdInput: input.inputItem,
    }));

    const result = await applyModelOnce(modelId, {
      modelApplicationInput: { inputs: inputMappings },
      sourceId: workbookId,
      datasourceId:
        selectedDatasource?.value.datasourceId ?? SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceId,
      datasourceClass:
        selectedDatasource?.value.datasourceClass ?? SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
    } as OneOffModelApplicationInputV1);

    if (!result) {
      return;
    }

    await addModelItemsToTrend(result.outputs as InstanceOutputMappingV1[]);
    successToast({ messageKey: 'MODELS.APPLY_ONCE_MESSAGE' });
    resetModel(MODEL_TYPES.TOOL);
    closeInvestigationTool();
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};
